import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import styles from './GymSettings.module.scss';
import MultiSelectList from '../composites/MultiSelectList';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';
import { locales } from '../../constants';
import ContentDrill from '../composites/ContentDrill';
import { useSessionData, useGlobalData, useUserAction } from '../../providers';
import { fetchGym } from '../../api/api-controllers/gyms-controller';
import { Gym, Exercise } from '../../types';
import { GymType } from '../../constants';

const texts = locales.en.components.gymSettings.gymSettings;

enum Features {
  athleteSwitch = 'Athlete Switch',
  automaticOffline = 'Automatic Offline',
  healthCheck = 'Device Health Check',
  signUp = 'Sign Up',
  programLibrary = 'Program Library',
  heightMeasurement = 'Height Measurement',
  workoutPreview = 'Workout Preview',
  persistConnection = 'Stay Connected After Workout',
  enableManualLoggingForAutomaticSets = 'Enable Manual Logging for Automatic Sets',
  useVideos = 'Enable Videos',
  enableDashboardLiveWorkouts = 'Enable Dashboard Live Workouts',
  enable5DigitPin = 'Enable 5 Digit Pin',
}

interface GymSettingsProps {
  gym?: Gym;
  onClose?: () => void;
}

const GymSettings: React.FC<GymSettingsProps> = ({ gym, onClose }) => {
  const { exercises } = useGlobalData();
  const { gym: currentGym } = useSessionData();
  const { updateGym } = useUserAction();
  const [loading, setLoading] = useState(false);
  const propGym = gym || currentGym;

  // Always load the latest gym data from the backend when this component mounts
  // or when the target gym changes, to avoid showing stale cached values.
  const [freshGym, setFreshGym] = useState<Gym | null>(null);
  const [isFetchingGym, setIsFetchingGym] = useState(true);

  useEffect(() => {
    const gymId = propGym?.id;
    if (!gymId) {
      setIsFetchingGym(false);
      return;
    }
    let active = true;
    setIsFetchingGym(true);
    fetchGym(gymId).then(({ data, success }) => {
      if (!active) return;
      if (success && data) {
        setFreshGym(data as unknown as Gym);
      }
      setIsFetchingGym(false);
    });
    return () => {
      active = false;
    };
  }, [propGym?.id]);

  const foundGym = freshGym ?? propGym;
  const [isSchoolType, setIsSchoolType] = useState(
    foundGym?.type === GymType.School,
  );
  const [activeTab, setActiveTab] = useState(0);

  const gymFeatures = useMemo(
    () => foundGym?.configuration?.enabledFeatures || {},
    [foundGym?.configuration?.enabledFeatures],
  );
  const features = Object.entries(Features).map(([key, value]) => ({
    id: key,
    name: value,
    value: gymFeatures?.[key as keyof typeof Features] || false,
  }));

  const computedSelectedFeatures = useMemo(
    () =>
      Object.entries(gymFeatures)
        .filter(([, value]) => value === true)
        .map(([key]) => ({
          id: key,
          name: Features[key as keyof typeof Features] ?? key,
        })),
    [gymFeatures],
  );

  const [selectedFeatures, setSelectedFeatures] = useState(
    computedSelectedFeatures,
  );

  useEffect(() => {
    setSelectedFeatures(computedSelectedFeatures);
  }, [computedSelectedFeatures]);

  const automaticExercises = useMemo(() => {
    return Object.values(exercises).filter(
      (exercise) => exercise.analysisOptions?.supported,
    );
  }, [exercises]);

  const sortedAutomaticExercises = useMemo(() => {
    return [...automaticExercises].sort((a, b) => a.name.localeCompare(b.name));
  }, [automaticExercises]);

  const [selectedHiddenExercises, setSelectedHiddenExercises] = useState<
    Exercise[]
  >([]);

  // Gateway config state
  // NOTE: These fields are legacy/metadata only. Tablets and dashboards always
  // connect to the centralized cloud gateway (`REALTIME_GATEWAY_URL`), so this
  // UI lets ops document overrides or historical settings per gym.
  const [gatewayHostname, setGatewayHostname] = useState<string>(
    foundGym?.configuration?.gateway?.hostname ?? '',
  );
  const [gatewayPort, setGatewayPort] = useState<string>(
    foundGym?.configuration?.gateway?.port != null
      ? String(foundGym.configuration.gateway.port)
      : '',
  );
  const [gatewayPath, setGatewayPath] = useState<string>(
    foundGym?.configuration?.gateway?.path ?? '',
  );
  useEffect(() => {
    const hiddenIds = foundGym?.configuration?.automaticExerciseIdsToHide || [];
    const selected = sortedAutomaticExercises.filter((ex) =>
      hiddenIds.includes(ex.id),
    );
    setSelectedHiddenExercises(selected);
  }, [
    sortedAutomaticExercises,
    foundGym?.id,
    foundGym?.configuration?.automaticExerciseIdsToHide,
  ]);

  useEffect(() => {
    // Sync gateway fields when gym changes
    setGatewayHostname(foundGym?.configuration?.gateway?.hostname ?? '');
    setGatewayPort(
      foundGym?.configuration?.gateway?.port != null
        ? String(foundGym.configuration.gateway.port)
        : '',
    );
    setGatewayPath(foundGym?.configuration?.gateway?.path ?? '');
  }, [foundGym?.id, foundGym?.configuration?.gateway]);

  useEffect(() => {
    setIsSchoolType(foundGym?.type === GymType.School);
  }, [foundGym?.type]);

  const renderConfiguration = () => {
    return (
      <div className={styles.configurationView}>
        <div className={styles.gymTypeToggle}>
          <FormControlLabel
            control={
              <Switch
                checked={isSchoolType}
                onChange={(e) => setIsSchoolType(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase': {
                    '&.Mui-checked': {
                      '& + .MuiSwitch-track': {
                        backgroundColor: 'var(--color-accent)',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: 'var(--border-color)',
                    opacity: 1,
                  },
                }}
              />
            }
            label="School"
          />
        </div>
        <MultiSelectList
          listClassName={styles.list}
          items={features}
          selectedItems={selectedFeatures}
          setSelectedItems={setSelectedFeatures}
          EmptyStateComponent={() => null}
          ItemComponent={({ data }) => (
            <div className={styles.gymMultiSelectItem}>
              <div className={styles.configNameWrapper}>
                <div className={styles.configName}>{data.name}</div>
              </div>
            </div>
          )}
        />
      </div>
    );
  };

  const renderGateway = () => {
    return (
      <div className={styles.gatewayView}>
        <div className={styles.row}>
          <TextField
            label="Hostname"
            placeholder="gateway.example.com"
            variant="outlined"
            size="small"
            fullWidth
            value={gatewayHostname}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setGatewayHostname(e.target.value)
            }
            className={styles.inputField}
          />
        </div>
        <div className={styles.row}>
          <TextField
            label="Port"
            placeholder="443"
            variant="outlined"
            size="small"
            fullWidth
            value={gatewayPort}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              if (/^[0-9]*$/.test(val)) setGatewayPort(val);
            }}
            className={styles.inputField}
            inputMode="numeric"
          />
        </div>
        <div className={styles.row}>
          <TextField
            label="Path"
            placeholder="/api"
            variant="outlined"
            size="small"
            fullWidth
            value={gatewayPath}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setGatewayPath(e.target.value)
            }
            className={styles.inputField}
          />
        </div>
      </div>
    );
  };

  const tabSliderTabs: TabSliderTab[] = [
    {
      label: 'Configuration',
      content: renderConfiguration(),
    },
    {
      label: 'Data Collection',
      content: (
        <div className={styles.configurationView}>
          <MultiSelectList
            listClassName={styles.list}
            items={sortedAutomaticExercises}
            selectedItems={selectedHiddenExercises}
            setSelectedItems={setSelectedHiddenExercises}
            EmptyStateComponent={() => null}
            ItemComponent={({ data }) => (
              <div className={styles.gymMultiSelectItem}>
                <div className={styles.configNameWrapper}>
                  <div className={styles.configName}>{data.name}</div>
                </div>
              </div>
            )}
          />
        </div>
      ),
    },
    {
      label: 'Gateway',
      content: renderGateway(),
    },
  ];

  const handleSubmit = async () => {
    if (!foundGym) return;
    setLoading(true);
    // Prepare gateway configuration (only include if any field provided)
    const parsedPort =
      gatewayPort.trim().length > 0
        ? parseInt(gatewayPort.trim(), 10)
        : undefined;
    const cleanedGateway = {
      hostname: gatewayHostname.trim() || undefined,
      port: Number.isFinite(parsedPort as number)
        ? (parsedPort as number)
        : undefined,
      path: gatewayPath.trim() || undefined,
    };
    const hasGatewayValues =
      cleanedGateway.hostname != null ||
      cleanedGateway.port != null ||
      cleanedGateway.path != null;
    await updateGym({
      ...foundGym,
      type: isSchoolType ? GymType.School : GymType.Commercial,
      configuration: {
        ...foundGym.configuration,
        enabledFeatures: {
          ...selectedFeatures.reduce(
            (acc, { id }) => ({ ...acc, [id]: true }),
            {},
          ),
        },
        automaticExerciseIdsToHide: selectedHiddenExercises.map((ex) => ex.id),
        ...(hasGatewayValues
          ? {
              gateway: {
                ...(foundGym.configuration?.gateway ?? {}),
                ...cleanedGateway,
              },
            }
          : {}),
      },
    });
    setLoading(false);
    onClose && onClose();
  };

  const renderSaveButton = () => (
    <Button
      className={styles.saveButton}
      disabled={loading}
      onClick={handleSubmit}
    >
      {loading ? <CircularProgress size={24} color="primary" /> : 'Save'}
    </Button>
  );

  // Unified save handler handles both tabs' data

  const renderContent = () => (
    <div className={styles.container}>
      <TabSlider
        tabs={tabSliderTabs}
        initialTab={0}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        rightActions={renderSaveButton()}
      />
    </div>
  );

  if (isFetchingGym) {
    const spinner = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <CircularProgress />
      </div>
    );
    if (onClose) return spinner;
    return (
      <ContentDrill title={texts.title} disableSearch isParent actions={[]}>
        {spinner}
      </ContentDrill>
    );
  }

  if (onClose) {
    return renderContent();
  }

  return (
    <ContentDrill title={texts.title} disableSearch isParent actions={[]}>
      {renderContent()}
    </ContentDrill>
  );
};

export default GymSettings;
